<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { McCheckbox } from "@maersk-global/mds-components-core";
import { useFiltersStore } from "@/store";
import { FilterType, FilterTypeEnum, useApplyFilter, FilterDefinition } from "@/features/filters";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  filter: FilterDefinition;
  filterType: FilterType;
  zonePrefix?: string;
}>();

const { key: filterKey, label: filterLabel = "", sectionLabel, groupId, groupLabel } = props.filter;

const { t } = useI18n();

const zone = props.zonePrefix || "default";
const filtersStore = useFiltersStore();
const { filtersApplied, panelSelectedFilters, applyPanelSelectedFilters } = filtersStore;
const { applyFilter, removeFilter } = useApplyFilter(props.filterType, props.zonePrefix);

const checkboxValue = ref(false);

const selectedFilter = computed(() => {
  const source = props.filterType === FilterTypeEnum.PANEL_FILTERS ? panelSelectedFilters : filtersApplied;
  const storeValue = source?.[zone]?.[filterKey];
  return {
    ...(storeValue || { label: "", value: false }),
    filterType: props.filterType
  };
});

const inputValue = computed(() => {
  return checkboxValue.value !== undefined ? checkboxValue.value : selectedFilter.value?.value || false;
});

watch(
  selectedFilter,
  (val) => {
    checkboxValue.value = val?.value || false;
  },
  { immediate: true }
);

const updatePanelFilters = (filterData) => {
  if (!panelSelectedFilters[zone]) panelSelectedFilters[zone] = {};
  applyPanelSelectedFilters(zone, filterKey, filterData);
};

const updateAppliedFilters = (filterData) => {
  if (filterData) {
    applyFilter(filterKey, filterData);
  } else {
    removeFilter(filterKey);
  }
};

// Build filter data
const buildFilterData = (checked: boolean) => {
  if (!checked) return null;

  return {
    filterKey,
    label: t(filterLabel),
    value: checked,
    filterType: props.filterType,
    ...(groupId ? { groupId } : {}),
    ...(groupLabel ? { groupLabel } : {}),
    ...(sectionLabel ? { sectionLabel } : {}),
    ...(filterLabel ? { filterLabel } : {})
  };
};

const onCheckboxChange = (e: Event) => {
  const checked = (e.target as HTMLInputElement).checked;
  checkboxValue.value = checked;

  const filterData = buildFilterData(checked);

  if (props.filterType === FilterTypeEnum.PANEL_FILTERS) {
    updatePanelFilters(filterData);
  } else {
    updateAppliedFilters(filterData);
  }
};
</script>

<template>
  <mc-checkbox
    slot="trigger"
    :name="filterKey"
    :label="t(filterLabel)"
    :checked="inputValue"
    data-testid="checkbox-filter"
    @change="onCheckboxChange"
  />
</template>
